import * as React from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { Expense, SourceDocument } from "@/types/tax";
import { expenseCategoryLabels } from "@/data/atoRules";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { SourceControl } from "@/components/domain/SourceControl";
import { claimableGstForExpense } from "@/lib/gst";
import { currency } from "@/lib/utils";

export function ExpenseTable({
  expenses,
  documents,
  periodLabel,
  onEdit,
  onDelete,
}: {
  expenses: Expense[];
  documents: SourceDocument[];
  periodLabel: string;
  onEdit: (expense: Expense) => void;
  onDelete: (id: string) => void;
}) {
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const previewDocument = documents.find((document) => document.id === previewId && document.storagePath);

  React.useEffect(() => {
    if (previewId && !documents.some((document) => document.id === previewId && document.storagePath)) {
      setPreviewId(null);
    }
  }, [documents, previewId]);

  function sourceDocumentFor(expense: Expense) {
    if (!expense.sourceDocumentId) return null;
    return documents.find((document) => document.id === expense.sourceDocumentId) || null;
  }

  function renderSource(expense: Expense) {
    const sourceDocument = sourceDocumentFor(expense);
    return (
      <SourceControl
        source={expense.entrySource === "document_scan" ? "document_scan" : "manual"}
        document={sourceDocument}
        expanded={Boolean(sourceDocument && previewId === sourceDocument.id)}
        onToggle={sourceDocument?.storagePath ? () => setPreviewId((current) => (current === sourceDocument.id ? null : sourceDocument.id)) : undefined}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Expense records</CardTitle>
        <CardDescription>Showing records for {periodLabel}. Each row keeps the source, business use, amount, and GST credit used in the worksheet.</CardDescription>
      </CardHeader>
      <CardContent>
        {expenses.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface p-6 text-center">
            <p className="font-semibold text-ink">No expenses for {periodLabel}</p>
            <p className="mt-1 text-sm text-muted">Change the reporting period or add fuel, phone, tolls, platform fees, or other business purchases.</p>
          </div>
        ) : (
          <>
          <div className="grid gap-3 sm:hidden">
            {expenses.map((expense) => (
              <div key={expense.id} className="rounded-lg border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{expense.supplier || "Not recorded"}</p>
                    <p className="mt-1 text-sm text-muted">{expense.date}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button type="button" variant="ghost" size="icon" aria-label={`Edit ${expenseCategoryLabels[expense.category]}`} onClick={() => onEdit(expense)}>
                      <Pencil className="size-4" aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-danger hover:text-danger"
                      aria-label={`Delete ${expenseCategoryLabels[expense.category]}`}
                      onClick={() => onDelete(expense.id)}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <MobileMetric label="Category" value={expenseCategoryLabels[expense.category]} />
                  <MobileMetric label="Amount" value={currency(expense.amount)} />
                  <MobileMetric label="Business use" value={`${expense.businessUsePercent}%`} />
                  <MobileMetric label="Claimable GST" value={currency(claimableGstForExpense(expense))} tone="credit" />
                  <MobileSource label="Source">{renderSource(expense)}</MobileSource>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[820px] border-separate border-spacing-0 text-left text-sm">
              <thead>
                <tr className="text-xs font-semibold text-muted">
                  <th className="border-b border-border px-3 py-2">Date</th>
                  <th className="border-b border-border px-3 py-2">Category</th>
                  <th className="border-b border-border px-3 py-2">Supplier</th>
                  <th className="border-b border-border px-3 py-2">Source</th>
                  <th className="border-b border-border px-3 py-2 text-right">Amount</th>
                  <th className="border-b border-border px-3 py-2 text-right">Business use</th>
                  <th className="border-b border-border px-3 py-2 text-right">Claimable GST</th>
                  <th className="border-b border-border px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id} className="text-ink hover:bg-surface">
                    <td className="border-b border-border px-3 py-3">{expense.date}</td>
                    <td className="border-b border-border px-3 py-3">{expenseCategoryLabels[expense.category]}</td>
                    <td className="border-b border-border px-3 py-3">{expense.supplier || "Not recorded"}</td>
                    <td className="border-b border-border px-3 py-3">{renderSource(expense)}</td>
                    <td className="tabular border-b border-border px-3 py-3 text-right">{currency(expense.amount)}</td>
                    <td className="tabular border-b border-border px-3 py-3 text-right">{expense.businessUsePercent}%</td>
                    <td className="tabular border-b border-border px-3 py-3 text-right font-semibold text-credit">
                      {currency(claimableGstForExpense(expense))}
                    </td>
                    <td className="border-b border-border px-3 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" aria-label={`Edit ${expenseCategoryLabels[expense.category]}`} onClick={() => onEdit(expense)}>
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-danger hover:text-danger"
                          aria-label={`Delete ${expenseCategoryLabels[expense.category]}`}
                          onClick={() => onDelete(expense.id)}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {previewDocument ? (
            <PdfPreview
              documentId={previewDocument.id}
              fileName={previewDocument.fileName}
              className="mt-4"
              autoOpen
              onClose={() => setPreviewId(null)}
            />
          ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MobileSource({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <div className="mt-1 flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function MobileMetric({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "credit" }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={`tabular mt-1 font-semibold ${tone === "credit" ? "text-credit" : "text-ink"}`}>{value}</p>
    </div>
  );
}
